<app-svg-definitions></app-svg-definitions>
<div class="jumbotron" title="Index Calculator" subtitle="Calculate the return of an index fund investment over time.">
  <div class="container pb-4">
    <div class="row">
      <div class="col-12">
        <h1 class="mt-3">Index Calculator</h1>
        <div class="d-flex align-items-start p-2">
          <div class="me-5 d-none d-md-block">
            <img src="/images/calculator.svg" height="200">
          </div>
          <div>
            <p class="text-white">Compare the past risk and return of your current investments to the IFA Individualized
              Index Portfolio recommended at the end of your Risk Capacity Survey or other IFA indexes.</p>

            <p class="float-start text-white user-select-none">The data provided in all charts and calculations on this
              page is hypothetical backtested performance and is not actual client performance. Only data for the IFC Index Portfolios, Catholic Values Target Retirement Model Portfolios, and IFC Risk Based Index Portfolios are shown
              net of IFC's highest advisory fee and the underlying fund expenses. All other data, including the
              IFC Indexes, does not reflect a deduction of advisory fees. None of the data reflects trading costs or
              taxes, which would have lowered performance by these costs. <a routerLink="/disclosures" class="text-light" >See more important disclosures</a>.</p>
          </div>
        </div>

      </div>
    </div>
  </div>


</div>
<div class="container-fluid container-lg pb-4">
  <div class="row">
    <div class="col-12 mt-4">
      <form [formGroup]="form" (ngSubmit)="submit()">

        <!-- Starting Assets -->
        <div class="mb-4 d-flex align-items-end gap-3 flex-wrap">
          <div>
            <label class="form-label fw-semibold">Starting Assets</label>
            <div class="input-group" style="max-width:280px">
              <span class="input-group-text">$</span>
              <input type="text" class="form-control" formControlName="startingAssets" mask="separator.0"
                thousandSeparator="," placeholder="0">
            </div>
          </div>

          <div class="d-flex align-items-center gap-1 mt-2">
            <mat-slide-toggle class="ifc-small-toggle" color="primary" [ngModel]="showFeeSchedule"
              [ngModelOptions]="{standalone: true}" (ngModelChange)="onShowFeeScheduleChange($event)">
            </mat-slide-toggle>
            <span class="small">Show Fee Schedule</span>
          </div>

          <div class="d-flex align-items-center gap-1 mt-2">
            <mat-slide-toggle class="ifc-small-toggle" color="primary" [(ngModel)]="includeAdvisorFee"
              [ngModelOptions]="{standalone: true}">
            </mat-slide-toggle>
            <span class="small">Advisor Fee</span>
          </div>
        </div>



        <!-- Fee Schedule -->
        <div *ngIf="showFeeSchedule" class="mt-3 mb-4">
          <h5 class="fw-semibold mb-3">IFC Advisory Fee Schedule</h5>
          <app-fee-schedule [initialBalance]="startingAssetsNumber" [showFlatFee]="true" [showDiscount]="true"
            [initialDiscount]="feeDiscount" [initialFlatFee]="includeAdvisorFee ? feeFlatFee : 0"
            (annualPercentageFee)="scheduleFeeRate = $event" (flatFeeChange)="onFlatFeeChange($event)"
            (discountChange)="onDiscountChange($event)"></app-fee-schedule>
        </div>

        <!-- Investment Choices -->
        <div class="mb-4 pick-section">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label class="form-label fw-semibold mb-0">Available Categories</label>
            <button type="button" class="btn btn-outline-secondary btn-sm pick-expand-btn ms-auto"
              [title]="expandedPicker ? 'Collapse lists' : 'Expand lists'" (click)="expandedPicker = !expandedPicker">
              {{expandedPicker ? '&minus; Collapse' : '&plus; Expand'}}
            </button>
          </div>

          <!-- Category visibility toggles -->
          <div class="row g-1 mb-2">
            <!-- Column 1: IFC -->
            <div class="col-12 col-lg-3 ">
              <ng-container
                *ngFor="let cat of ['IFC Index Portfolios', 'IFC Index Portfolios (Glide Path)', 'IFC Indexes']">
                <div class="d-flex align-items-center gap-1" style="min-height:1.4rem">
                  <mat-slide-toggle class="ifc-tiny-toggle" color="primary" [checked]="isCategoryVisible(cat)"
                    (change)="toggleCategoryVisible(cat)">
                  </mat-slide-toggle>
                  <span class="pick-cat-toggle-label" (click)="toggleCategoryVisible(cat)">{{cat}}</span>
                  <span *ngIf="selectedCountInCategory(cat) > 0"
                    class="badge bg-primary pick-cat-toggle-badge ms-1">{{selectedCountInCategory(cat)}}</span>
                </div>
              </ng-container>
            </div>
            <!-- Column 2: Target Date -->
            <div class="col-12 col-lg-3">
              <ng-container *ngFor="let cat of ['Target Date Retirement', 'Target Date Benchmarks']">
                <div class="d-flex align-items-center gap-1" style="min-height:1.4rem">
                  <mat-slide-toggle class="ifc-tiny-toggle" color="primary" [checked]="isCategoryVisible(cat)"
                    (change)="toggleCategoryVisible(cat)">
                  </mat-slide-toggle>
                  <span class="pick-cat-toggle-label" (click)="toggleCategoryVisible(cat)">{{cat}}</span>
                  <span *ngIf="selectedCountInCategory(cat) > 0"
                    class="badge bg-primary pick-cat-toggle-badge ms-1">{{selectedCountInCategory(cat)}}</span>
                </div>
              </ng-container>
            </div>
            <!-- Column 3: Dimensional -->
            <div class="col-12 col-lg-6">
              <ng-container
                *ngFor="let cat of ['Dimensional Mutual Fund Portfolios', 'Dimensional Index Portfolios', 'Dimensional Prospectus Benchmark Portfolios']">
                <div class="d-flex align-items-center gap-1" style="min-height:1.4rem">
                  <mat-slide-toggle class="ifc-tiny-toggle" color="primary" [checked]="isCategoryVisible(cat)"
                    (change)="toggleCategoryVisible(cat)">
                  </mat-slide-toggle>
                  <span class="pick-cat-toggle-label" (click)="toggleCategoryVisible(cat)">{{cat}}</span>
                  <span *ngIf="selectedCountInCategory(cat) > 0"
                    class="badge bg-primary pick-cat-toggle-badge ms-1">{{selectedCountInCategory(cat)}}</span>
                </div>
              </ng-container>
            </div>
          </div>

          <!-- Category slider — one card per category -->
          <app-banner-slider [class]="'pick-slider-wrap' + (expandedPicker ? ' pick-slider-expanded' : '')">
            <app-banner-card *ngFor="let cat of investmentCategories" class="pt-0 pick-cat-card"
              [style.display]="isCategoryVisible(cat) ? '' : 'none'"
              [css]="'cursor:default; width:' + (categoryPickerWidth[cat] || '280px') + '; min-width:' + (categoryPickerWidth[cat] || '280px')">

              <!-- ── Card header: icon strip + name + badges ── -->
              <div class="pick-cat-header">

                <!-- Category icon strip -->
                <div class="pick-cat-icon-strip"
                  [ngClass]="'pick-icon-' + (categoryFeeType[cat] === 'net' ? 'net' : 'gross')">
                  <!-- IFC Index Portfolios -->
                  <svg *ngIf="cat === 'IFC Index Portfolios'" viewBox="0 0 56 40" fill="none">
                    <rect x="2" y="26" width="8" height="12" rx="1.5" fill="currentColor" opacity=".35" />
                    <rect x="13" y="18" width="8" height="20" rx="1.5" fill="currentColor" opacity=".55" />
                    <rect x="24" y="10" width="8" height="28" rx="1.5" fill="currentColor" opacity=".75" />
                    <rect x="35" y="4" width="8" height="34" rx="1.5" fill="currentColor" opacity=".95" />
                    <polyline points="2,30 13,22 24,14 35,8 46,4" stroke="currentColor" stroke-width="2" fill="none"
                      stroke-linecap="round" />
                  </svg>
                  <!-- IFC Index Portfolios (Glide Path) -->
                  <svg *ngIf="cat === 'IFC Index Portfolios (Glide Path)'" viewBox="0 0 56 40" fill="none">
                    <path d="M4,6 Q20,8 30,20 Q40,32 52,36" stroke="currentColor" stroke-width="2.5" fill="none"
                      stroke-linecap="round" />
                    <rect x="4" y="28" width="48" height="8" rx="2" fill="currentColor" opacity=".15" />
                    <circle cx="4" cy="6" r="3" fill="currentColor" />
                    <circle cx="52" cy="36" r="3" fill="currentColor" />
                    <text x="28" y="24" font-size="7" fill="currentColor" text-anchor="middle" opacity=".7">GLIDE
                      PATH</text>
                  </svg>
                  <!-- IFC Indexes -->
                  <svg *ngIf="cat === 'IFC Indexes'" viewBox="0 0 56 40" fill="none">
                    <line x1="10" y1="4" x2="10" y2="38" stroke="currentColor" stroke-width="2.5"
                      stroke-linecap="round" />
                    <line x1="10" y1="38" x2="50" y2="38" stroke="currentColor" stroke-width="2.5"
                      stroke-linecap="round" />
                    <rect x="15" y="22" width="7" height="16" rx="1" fill="currentColor" opacity=".5" />
                    <rect x="26" y="14" width="7" height="24" rx="1" fill="currentColor" opacity=".7" />
                    <rect x="37" y="8" width="7" height="30" rx="1" fill="currentColor" opacity=".9" />
                  </svg>
                  <!-- Target Date Retirement -->
                  <svg *ngIf="cat === 'Target Date Retirement'" viewBox="0 0 56 40" fill="none">
                    <rect x="6" y="10" width="44" height="28" rx="4" stroke="currentColor" stroke-width="2"
                      fill="currentColor" fill-opacity=".1" />
                    <line x1="16" y1="6" x2="16" y2="16" stroke="currentColor" stroke-width="2.5"
                      stroke-linecap="round" />
                    <line x1="40" y1="6" x2="40" y2="16" stroke="currentColor" stroke-width="2.5"
                      stroke-linecap="round" />
                    <line x1="6" y1="20" x2="50" y2="20" stroke="currentColor" stroke-width="1.5" />
                    <text x="28" y="33" font-size="11" font-weight="bold" fill="currentColor"
                      text-anchor="middle">$</text>
                  </svg>
                  <!-- Target Date Benchmarks -->
                  <svg *ngIf="cat === 'Target Date Benchmarks'" viewBox="0 0 56 40" fill="none">
                    <!-- Pole -->
                    <line x1="28" y1="4" x2="28" y2="36" stroke="currentColor" stroke-width="2.5"
                      stroke-linecap="round" />
                    <!-- Base -->
                    <line x1="18" y1="36" x2="38" y2="36" stroke="currentColor" stroke-width="2.5"
                      stroke-linecap="round" />
                    <!-- Crossbar -->
                    <line x1="10" y1="13" x2="46" y2="13" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" />
                    <!-- Left chain -->
                    <line x1="10" y1="13" x2="10" y2="24" stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round" opacity=".7" />
                    <!-- Right chain -->
                    <line x1="46" y1="13" x2="46" y2="24" stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round" opacity=".7" />
                    <!-- Left pan bowl (centered at x=10, sweeps downward) -->
                    <path d="M4,24 A6,5 0 0 0 16,24" stroke="currentColor" stroke-width="1.5" fill="currentColor"
                      fill-opacity=".35" />
                    <!-- Right pan bowl (centered at x=46, sweeps downward) -->
                    <path d="M40,24 A6,5 0 0 0 52,24" stroke="currentColor" stroke-width="1.5" fill="currentColor"
                      fill-opacity=".35" />
                  </svg>
                  <!-- Dimensional Index Portfolios -->
                  <svg *ngIf="cat === 'Dimensional Index Portfolios'" viewBox="0 0 56 40" fill="none">
                    <circle cx="28" cy="20" r="16" fill="currentColor" opacity=".12" stroke="currentColor"
                      stroke-width="2" />
                    <text x="28" y="17" font-size="9" font-weight="bold" fill="currentColor"
                      text-anchor="middle">DFA</text>
                    <text x="28" y="28" font-size="7.5" fill="currentColor" text-anchor="middle"
                      opacity=".8">INDEX</text>
                  </svg>
                  <!-- Dimensional Prospectus Benchmark Portfolios -->
                  <svg *ngIf="cat === 'Dimensional Prospectus Benchmark Portfolios'" viewBox="0 0 56 40" fill="none">
                    <rect x="3" y="4" width="50" height="32" rx="4" fill="currentColor" opacity=".12"
                      stroke="currentColor" stroke-width="2" />
                    <text x="28" y="17" font-size="9" font-weight="bold" fill="currentColor"
                      text-anchor="middle">DFA</text>
                    <text x="28" y="28" font-size="7" fill="currentColor" text-anchor="middle"
                      opacity=".8">BENCHMARK</text>
                  </svg>
                  <!-- Dimensional Mutual Fund Portfolios -->
                  <svg *ngIf="cat === 'Dimensional Mutual Fund Portfolios'" viewBox="0 0 56 40" fill="none">
                    <polygon points="28,3 53,20 28,37 3,20" fill="currentColor" opacity=".12" stroke="currentColor" stroke-width="2"/>
                    <text x="28" y="17" font-size="9" font-weight="bold" fill="currentColor"
                      text-anchor="middle">DFA</text>
                    <text x="28" y="28" font-size="6.5" fill="currentColor" text-anchor="middle"
                      opacity=".8">MUT. FUND</text>
                  </svg>
                </div>

                <!-- Name row -->
                <div class="d-flex align-items-center gap-1 px-2 pt-1 pb-0">
                  <span class="pick-cat-name fw-semibold" style="font-size:.82rem; line-height:1.2">{{cat}}</span>
                  <span class="badge bg-primary ms-auto flex-shrink-0 pick-fee-badge-cnt"
                    *ngIf="selectedCountInCategory(cat) > 0">{{selectedCountInCategory(cat)}}</span>
                </div>

                <!-- Fee badge -->
                <div class="d-flex align-items-center gap-1 px-2 py-1">
                  <span class="badge pick-fee-badge flex-shrink-0" [class.pick-fee-net]="categoryFeeType[cat] === 'net'"
                    [class.pick-fee-gross]="categoryFeeType[cat] === 'gross'">
                    {{categoryFeeType[cat] === 'net' ? 'Net of IFC Fees' : 'Gross of IFC Fees'}}
                  </span>
                </div>
              </div>

              <!-- ── Card body: description + controls + list ── -->
              <div class="pick-scroll-body">
                <div class="pick-cat-body">
                  <!-- Description -->
                  <p class="pick-cat-desc small text-muted px-2 mb-1">
                    {{categoryDescriptionsAll[cat]}}
                  </p>
                  <!-- Quick-select controls -->
                  <div class="d-flex align-items-center gap-1 px-2 mb-1">
                    <button type="button" class="btn btn-link btn-sm p-0" style="font-size:.75rem; white-space:nowrap"
                      (click)="toggleCategory(cat); $event.stopPropagation()">
                      {{allInCategorySelected(cat) ? 'None' : 'All'}}
                    </button>
                    <!-- <button *ngIf="cat === PORTFOLIOS_CATEGORY || cat === GLIDE_PATH_CATEGORY" type="button"
                      class="btn btn-link btn-sm p-0" style="font-size:.75rem; white-space:nowrap"
                      (click)="showAllPortfolios = !showAllPortfolios; $event.stopPropagation()">
                      {{showAllPortfolios ? 'Every 5th' : 'All 100'}}
                    </button> -->
                  </div>
                  <!-- Scrollable investment list -->
                  <div class="pick-inv-list">
                    <div *ngFor="let inv of visibleInvestmentsByCategory(cat)"
                      class="pick-inv-item d-flex align-items-center px-2 py-1"
                      [class.pick-inv-item--selected]="isSelected(inv.name)"
                      (click)="toggleInvestment(inv.name); $event.stopPropagation()">
                      <input class="form-check-input me-2 flex-shrink-0" type="checkbox"
                        [checked]="isSelected(inv.name)" style="pointer-events:none">
                      <app-svg-sprite-v2 [title]="inv.name" [width]="20" [height]="20"
                        [hash]="'#icon-' + svgSymbol(inv.symbol)" style="margin:0 4px 0 0; flex-shrink:0">
                      </app-svg-sprite-v2>
                      <span class="small user-select-none text-truncate">{{inv.name}}</span>
                    </div>
                  </div>
                </div>
              </div>

            </app-banner-card>
          </app-banner-slider>

          <!-- Selected chips -->
          <div class="d-flex flex-wrap gap-2 mt-2 align-items-center">
            <span *ngFor="let name of selectedInvestmentNames"
              class="badge rounded-pill bg-primary d-flex align-items-center gap-1"
              style="font-size:.85rem; font-weight:400">
              {{name}}
              <button type="button" class="btn-close btn-close-white" style="font-size:.6rem"
                (click)="removeSelectedInvestment(name)" aria-label="Remove"></button>
            </span>
            <span *ngIf="!selectedInvestmentNames.length" class="text-muted small">
              No funds/indexes selected.
            </span>
            <button *ngIf="selectedInvestmentNames.length" type="button" class="btn btn-outline-secondary btn-sm ms-1"
              (click)="selectedInvestmentNames = []">
              Clear
            </button>
          </div>
        </div>

        <!-- Time Periods -->
        <div class="mb-4">
          <label class="form-label fw-semibold">Time Periods</label>

          <!-- Preset toggles (shared, appear once) -->
          <div class="d-flex flex-wrap gap-1 mb-3">
            <button *ngFor="let p of presets" type="button" class="btn btn-sm"
              [class.btn-primary]="activePresets.has(p.label)"
              [class.btn-outline-secondary]="!activePresets.has(p.label)" (click)="togglePreset(p)">{{p.label}}</button>
            <div class="vr mx-1"></div>
            <button type="button" class="btn btn-sm" [class.btn-secondary]="allPresetsActive"
              [class.btn-outline-secondary]="!allPresetsActive" (click)="toggleAllPresets()">
              {{allPresetsActive ? 'Deselect All' : 'Select All'}}
            </button>
            <div class="vr mx-1"></div>
            <button type="button" class="btn btn-outline-primary btn-sm" (click)="addTimePeriod()">
              + Custom Period
            </button>
          </div>

          <!-- Manual period rows -->
          <div formArrayName="timePeriods">
            <div *ngFor="let tp of timePeriods.controls; let i = index" [formGroupName]="i">
              <div class="row g-2 align-items-center mb-2">
                <ng-container *ngIf="tp.get('isCustom')?.value">
                  <div class="col-auto">
                    <span class="text-muted small">From</span>
                  </div>
                  <div class="col-auto">
                    <select class="form-select form-select-sm" formControlName="startMonth">
                      <option *ngFor="let m of startMonthsFor(tp.get('startYear')?.value)" [value]="m.value">{{m.label}}
                      </option>
                    </select>
                  </div>
                  <div class="col-auto">
                    <input type="number" class="form-control form-control-sm" formControlName="startYear"
                      style="width:90px" placeholder="Year" [min]="dataRangeMin.year" [max]="dataRangeMax.year">
                  </div>
                  <div class="col-auto">
                    <span class="text-muted small">To</span>
                  </div>
                  <div class="col-auto">
                    <select class="form-select form-select-sm" formControlName="endMonth">
                      <option *ngFor="let m of endMonthsFor(tp.get('endYear')?.value)" [value]="m.value">{{m.label}}
                      </option>
                    </select>
                  </div>
                  <div class="col-auto">
                    <input type="number" class="form-control form-control-sm" formControlName="endYear"
                      style="width:90px" placeholder="Year" [min]="dataRangeMin.year" [max]="dataRangeMax.year">
                  </div>
                </ng-container>
                <div class="col-auto" *ngIf="tp.get('isCustom')?.value">
                  <button type="button" class="btn btn-outline-danger btn-sm" (click)="removeTimePeriod(i)">
                    &minus;
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>

        <button type="submit" class="btn btn-primary" [disabled]="loading">
          <ng-container *ngIf="loading">
            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            {{loadingDone}} / {{loadingTotal}}
          </ng-container>
          <ng-container *ngIf="!loading">Calculate</ng-container>
        </button>

        <!-- API error -->
        <div *ngIf="apiError" class="alert-danger mt-3 d-flex align-items-start gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="flex-shrink-0 mt-1"
            viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
            <path
              d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
          </svg>
          <div>
            <div class="fw-semibold">Unable to load results</div>
            <div class="small mt-1">We couldn't retrieve the data for your selection. Please check your inputs and try
              again. If the problem persists, the data service may be temporarily unavailable.</div>
          </div>
        </div>

        <!-- Debug: built URL -->
        <!-- <div *ngIf="apiUrl" class="mt-3">
          <small class="text-muted text-break">{{apiUrl}}</small>
        </div> -->



        <!-- Results -->
        <div *ngIf="resultPeriods.length" class="mt-4">
          <h5 class="fw-semibold mb-3">Results</h5>

          <!-- Partial data warnings -->
          <div *ngIf="hasAnyPartialData()" class="border py-2 mb-3 px-2" role="alert">
            <strong>&#9888; Partial Data:</strong> The following fund(s) do not have data for the full selected period.
            Results for those funds cover only the available history.
            <ul class="mb-0 mt-1">
              <li *ngFor="let f of partialDataFundSummary">
                <strong>{{ f.name }}</strong> &mdash; data available from {{ f.inceptionDate | date:'MMMM d, y' }}
              </li>
            </ul>
          </div>

          <!-- Terminated data warnings -->
          <div *ngIf="hasAnyTerminatedData()" class="border py-2 mb-3 px-2" role="alert">
            <strong>&#9888; Data Series Truncated:</strong>
            <ul class="mb-0 mt-1">
              <li *ngFor="let f of terminatedDataFundSummary">
                <strong>{{ f.name }}</strong> data series ends {{ f.terminationDate | date:'M/yyyy' }}. Date range
                exceeds data series range.
              </li>
            </ul>
          </div>

          <!-- Comparison table: investments × periods -->
          <div *ngIf="resultPeriods.length > 1" class="table-responsive mb-4">
            <table class="table table-sm table-bordered table-hover table-striped align-middle mb-0">
              <thead class="table-light sticky-top" style="z-index:2">
                <tr>
                  <th style="white-space:nowrap">
                    Investment
                    <button type="button" class="btn btn-sm btn-link p-0 ms-1 text-decoration-none"
                      [title]="resultSortMode === 'vintage' ? 'Sorted by vintage year — click for default order' : 'Click to sort by vintage year'"
                      (click)="toggleResultSort()">
                      <span *ngIf="resultSortMode === 'default'" style="font-size:.85em; opacity:.55">&#8693;</span>
                      <span *ngIf="resultSortMode === 'vintage'"
                        style="font-size:.85em">&#8595;&nbsp;<small>vintage</small></span>
                    </button>
                  </th>
                  <th *ngIf="hasAnyPartialData()" class="text-end" style="white-space:nowrap">Inception Date</th>
                  <th *ngIf="hasAnyTerminatedData()" class="text-end" style="white-space:nowrap">Last Data</th>
                  <th *ngFor="let period of resultPeriods" class="text-end">
                    {{ period.label }}
                    <div class="text-muted fw-normal" style="font-size:.8em; ">
                      {{ period.startDate | date:"M/'1'/yyyy" }} – {{ period.endDate | date:'M/d/yyyy' }}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of sortedPivotRows">
                  <td class="d-flex align-items-center gap-1 text-nowrap">
                    <app-svg-sprite-v2 [title]="row.portfolioName" [width]="20" [height]="20"
                      [hash]="'#icon-' + svgSymbol(row.portfolioSymbol)" style="flex-shrink:0"></app-svg-sprite-v2>
                    {{ row.portfolioName }}
                  </td>
                  <td *ngIf="hasAnyPartialData()" class="text-end text-nowrap" style="font-size:.85em">
                    {{ row.inceptionDate ? (row.inceptionDate | date:'M/d/yyyy') : '—' }}
                  </td>
                  <td *ngIf="hasAnyTerminatedData()" class="text-end text-nowrap" style="font-size:.85em">
                    {{ row.terminationDate ? (row.terminationDate | date:'M/d/yyyy') : '—' }}
                  </td>
                  <td *ngFor="let cell of row.cells; let pi = index" class="text-end">
                    <ng-container *ngIf="cell; else noData">
                      <ng-container *ngIf="cell.months >= 12">
                        <span
                          [title]="cell.annualizedReturn !== null ? (cell.annualizedReturn | number:'1.2-5') + '%' : 'No data'">{{ cell.annualizedReturn | number:'1.2-2' }}%</span>
                      </ng-container>
                      <ng-container *ngIf="cell.months < 12">
                        <span
                          [title]="cell.totalReturn !== null ? (cell.totalReturn | number:'1.2-5') + '%' : 'No data'">{{ cell.totalReturn | number:'1.2-2' }}%</span>
                      </ng-container>
                    </ng-container>
                    <ng-template #noData>—</ng-template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h6 class="fw-semibold mb-3">Period Details:</h6>

          <!-- Period tabs nav -->
          <ul class="nav nav-tabs mb-0" style="overflow-x:auto; overflow-y:hidden; flex-wrap:nowrap; -webkit-overflow-scrolling:touch">
            <li *ngFor="let period of resultPeriods; let i = index" class="nav-item">
              <button class="nav-link" [class.active]="activePeriodTab === i" type="button"
                (click)="setActivePeriodTab(i)">
                {{ period.label }}
                <div class="fw-normal" style="font-size:.75em; opacity:.75; white-space:nowrap;">
                  {{ period.startDate | date:"M/'1'/yy" }} – {{ period.endDate | date:'M/d/yy' }}
                </div>
              </button>
            </li>
          </ul>

          <!-- Period tab pane (single, data-driven) -->
          <div class="tab-content border border-top-0 rounded-bottom p-3">
            <ng-container *ngIf="resultPeriods[activePeriodTab] as period">

              <!-- Date range sub-heading -->
              <p class="text-muted mb-3" style="font-size:.85em;">
                {{ period.startDate | date:"MMM '1', y" }} – {{ period.endDate | date:'MMM d, y' }}
              </p>

              <!-- Summary table -->
              <div class="table-responsive mb-3">
                <table class="table table-sm table-bordered table-hover align-middle mb-0">
                  <thead class="table-light sticky-top" style="z-index:2">
                    <tr>
                      <th style="white-space:nowrap">
                        Name
                        <button type="button" class="btn btn-sm btn-link p-0 ms-1 text-decoration-none"
                          [title]="resultSortMode === 'vintage' ? 'Sorted by vintage year — click for default order' : 'Click to sort by vintage year'"
                          (click)="toggleResultSort()">
                          <span *ngIf="resultSortMode === 'default'" style="font-size:.85em; opacity:.55">&#8693;</span>
                          <span *ngIf="resultSortMode === 'vintage'"
                            style="font-size:.85em">&#8595;&nbsp;<small>vintage</small></span>
                        </button>
                      </th>
                      <th *ngIf="hasPartialData(activePeriodTab)" class="text-end" style="white-space:nowrap">
                        Inception Date</th>
                      <th *ngIf="hasTerminatedData(activePeriodTab)" class="text-end" style="white-space:nowrap">
                        Last Data</th>
                      <th class="text-end">Total Return</th>
                      <th *ngIf="period.months >= 12" class="text-end">Ann. Return</th>
                      <th class="text-end">Std Dev</th>
                      <th *ngIf="period.months >= 12" class="text-end">Ann. Std Dev</th>
                      <th class="text-end">Ending Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let row of sortedPeriodRows(displayedRows[activePeriodTab] || period.rows)">
                      <td class="d-flex align-items-center gap-1">
                        <app-svg-sprite-v2 [title]="row.portfolioName" [width]="20" [height]="20"
                          [hash]="'#icon-' + svgSymbol(row.portfolioSymbol)" style="flex-shrink:0"></app-svg-sprite-v2>
                        {{ row.portfolioName }}
                      </td>
                      <td *ngIf="hasPartialData(activePeriodTab)" class="text-end text-nowrap" style="font-size:.85em">
                        {{ row.inceptionDate ? (row.inceptionDate | date:'M/d/yyyy') : '—' }}
                      </td>
                      <td *ngIf="hasTerminatedData(activePeriodTab)" class="text-end text-nowrap"
                        style="font-size:.85em">
                        {{ row.terminationDate ? (row.terminationDate | date:'M/d/yyyy') : '—' }}
                      </td>
                      <td class="text-end"
                        [title]="row.totalReturn !== null ? (row.totalReturn | number:'1.2-5') + '%' : 'No data'">
                        {{ row.totalReturn | number:'1.2-2' }}%</td>
                      <td *ngIf="period.months >= 12" class="text-end"
                        [title]="row.annualizedReturn !== null ? (row.annualizedReturn | number:'1.2-5') + '%' : 'No data'">
                        {{ row.annualizedReturn | number:'1.2-2' }}%</td>
                      <td class="text-end"
                        [title]="row.stdDev !== null ? (row.stdDev | number:'1.2-5') + '%' : 'No data'">
                        {{ row.stdDev !== null ? (row.stdDev | number:'1.2-2') + '%' : '—' }}</td>
                      <td *ngIf="period.months >= 12" class="text-end"
                        [title]="row.annualizedStdDev !== null ? (row.annualizedStdDev | number:'1.2-5') + '%' : 'No data'">
                        {{ row.annualizedStdDev !== null ? (row.annualizedStdDev | number:'1.2-2') + '%' : '—' }}</td>
                      <td class="text-end"
                        [title]="row.endingDollar !== null ? (row.endingDollar | currency:'USD':'symbol':'1.2-2') : 'No data'">
                        {{ row.endingDollar !== null && row.endingDollar > 10000 ? (row.endingDollar | currency:'USD':'symbol':'1.0-0') : (row.endingDollar | currency) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Risk/Return scatter plot -->
              <div class="d-flex flex-wrap align-items-center gap-3 mb-2">
                <span class="fw-semibold" style="font-size:.9em;">Scatter Plot</span>
                <div class="btn-group btn-group-sm" role="group" title="Scatter plot axis scale">
                  <button type="button" class="btn" [class.btn-secondary]="scatterOriginMode === 'fit'"
                    [class.btn-outline-secondary]="scatterOriginMode !== 'fit'" (click)="toggleScatterOrigin()">Fit to
                    Data</button>
                  <button type="button" class="btn" [class.btn-secondary]="scatterOriginMode === 'origin'"
                    [class.btn-outline-secondary]="scatterOriginMode !== 'origin'" (click)="toggleScatterOrigin()">Show
                    (0,0)</button>
                </div>
              </div>
              <div id="scatter-container" class="p-2 w-100 mb-3" style="position:relative"></div>

              <!-- Monthly detail heading + toggles -->
              <div class="d-flex flex-wrap align-items-center gap-2 gap-md-5 mb-2">
                <span class="fw-semibold"
                  style="font-size:.9em;">{{ isAnnualDetail(activePeriodTab) ? 'Annual' : 'Monthly' }} Detail</span>
                <div *ngIf="period.months >= 12" class="btn-group btn-group-sm" role="group">
                  <button type="button" class="btn" [class.btn-secondary]="!isAnnualDetail(activePeriodTab)"
                    [class.btn-outline-secondary]="isAnnualDetail(activePeriodTab)"
                    (click)="setDetailGranularity(activePeriodTab, 'monthly')">Monthly</button>
                  <button type="button" class="btn" [class.btn-secondary]="isAnnualDetail(activePeriodTab)"
                    [class.btn-outline-secondary]="!isAnnualDetail(activePeriodTab)"
                    (click)="setDetailGranularity(activePeriodTab, 'annual')">Annual</button>
                </div>
                <div *ngIf="isAnnualDetail(activePeriodTab)" class="btn-group btn-group-sm" role="group"
                  title="How 12-month cycles are aligned">
                  <button type="button" class="btn" [class.btn-secondary]="!isRollingCycle(activePeriodTab)"
                    [class.btn-outline-secondary]="isRollingCycle(activePeriodTab)"
                    (click)="setAnnualCycleMode(activePeriodTab, 'calendar')">Dec&nbsp;31</button>
                  <button type="button" class="btn" [class.btn-secondary]="isRollingCycle(activePeriodTab)"
                    [class.btn-outline-secondary]="!isRollingCycle(activePeriodTab)"
                    (click)="setAnnualCycleMode(activePeriodTab, 'rolling')">12-Month&nbsp;Cycles</button>
                </div>
                <div class="d-flex gap-2 align-items-center">
                  <div class="d-flex align-items-center ">
                    <mat-slide-toggle class="ifc-small-toggle" color="primary"
                      [checked]="isColActive(activePeriodTab, 'value')"
                      (change)="toggleMonthlyView(activePeriodTab, 'value')">
                    </mat-slide-toggle>
                    <span class="small">Value</span>
                  </div>
                  <div class="d-flex align-items-center ">
                    <mat-slide-toggle class="ifc-small-toggle" color="primary"
                      [checked]="isColActive(activePeriodTab, 'return')"
                      (change)="toggleMonthlyView(activePeriodTab, 'return')">
                    </mat-slide-toggle>
                    <span class="small">Return</span>
                  </div>
                  <div class="d-flex align-items-center ">
                    <mat-slide-toggle class="ifc-small-toggle" color="primary"
                      [checked]="isColActive(activePeriodTab, 'equity')"
                      (change)="toggleMonthlyView(activePeriodTab, 'equity')">
                    </mat-slide-toggle>
                    <span class="small">Equity %</span>
                  </div>
                  <!-- Log Scale: only visible when the Value series is active -->
                  <div class="d-flex align-items-center" *ngIf="isColActive(activePeriodTab, 'value')">
                    <mat-slide-toggle class="ifc-small-toggle" color="primary" [checked]="isLogScale(activePeriodTab)"
                      (change)="toggleLogScale(activePeriodTab)">
                    </mat-slide-toggle>
                    <span class="small">Log Scale</span>
                  </div>
                </div>
                <!-- <div class="d-flex align-items-center gap-2 ms-auto">
                  <span class="small text-muted">Line</span>
                  <input type="range" min="0.5" max="6" step="0.5"
                         [value]="chartStrokeWidth"
                         (input)="chartStrokeWidth = +$any($event.target).value; renderCharts()"
                         style="width:90px; accent-color: var(--bs-primary)">
                  <span class="small text-muted" style="min-width:2ch">{{chartStrokeWidth}}</span>
                  <span class="small text-muted ms-2">Font</span>
                  <input type="range" min="8" max="20" step="1"
                         [value]="chartAxisFontSize"
                         (input)="chartAxisFontSize = +$any($event.target).value; renderCharts()"
                         style="width:90px; accent-color: var(--bs-primary)">
                  <span class="small text-muted" style="min-width:2ch">{{chartAxisFontSize}}</span>
                </div> -->
              </div>

              <!-- D3 chart -->
              <div id="chart-container" class="p-2 w-100 mb-3"></div>

              <!-- Monthly table fee toggle -->
              <div class="d-flex align-items-center gap-1 mb-2 justify-content-end">
                <mat-slide-toggle class="ifc-small-toggle" color="primary" [ngModel]="showFeeCol"
                  (ngModelChange)="onShowFeeColChange($event)" [ngModelOptions]="{standalone: true}">
                </mat-slide-toggle>
                <span class="small">Show IFC Advisory Fee</span>
                <!-- <span class="small text-muted">(according to the IFC Fee Schedule)</span> -->
              </div>

              <!-- Monthly detail table -->
              <div class="table-responsive mb-3">
                <table class="table table-sm table-bordered align-middle mb-0" style="font-size:.85em;">
                  <thead class="table-light" style="position:sticky; top:0; z-index:2">
                    <tr>
                      <th rowspan="2" class="text-center align-middle">
                        {{ isAnnualDetail(activePeriodTab) ? 'Annual' : 'Monthly' }} Period Ending</th>
                      <th *ngFor="let col of sortedPeriodColumns(activePeriodTab)" class="text-center"
                        [attr.colspan]="activeDetailCols(activePeriodTab).length + (showFeeCol ? 1 : 0)">
                        <app-svg-sprite-v2 [title]="col.row.portfolioName" [width]="18" [height]="18"
                          [hash]="'#icon-' + svgSymbol(col.row.portfolioSymbol)"
                          style="flex-shrink:0; vertical-align:middle; margin-right:3px"></app-svg-sprite-v2>
                        {{ col.row.portfolioName }}
                      </th>
                    </tr>
                    <tr>
                      <ng-container *ngFor="let pcol of sortedPeriodColumns(activePeriodTab)">
                        <th *ngFor="let col of activeDetailCols(activePeriodTab)" class="text-center fw-normal"
                          style="font-size:.8em; white-space:nowrap">
                          {{ col === 'value' ? 'Value' : col === 'return' ? 'Return' : 'Equity %' }}
                        </th>
                        <th *ngIf="showFeeCol" class="text-center fw-normal" style="font-size:.8em; white-space:nowrap">
                          Fee&nbsp;{{ isAnnualDetail(activePeriodTab) ? '%/yr' : '%/mo' }}</th>
                      </ng-container>
                    </tr>
                  </thead>
                  <tbody>
                    <ng-container
                      *ngFor="let mrow of (isAnnualDetail(activePeriodTab) ? periodAnnualRows(activePeriodTab) : periodMonthlyRows(activePeriodTab)); let mi = index">
                      <tr *ngIf="mi < 24 || showAllMonthlyRows[activePeriodTab]">
                        <td class="text-center text-nowrap">{{ mrow.date | date:'M/dd/yyyy' }}</td>
                        <ng-container *ngFor="let col of sortedPeriodColumns(activePeriodTab)">
                          <ng-container *ngIf="mrow.values[col.index] as v">
                            <td *ngIf="isColActive(activePeriodTab, 'value')" class="text-center"
                              [title]="v.return !== null ? (v.dollarValue | currency:'USD':'symbol':'1.2-2') : 'N/A'">
                              <ng-container *ngIf="v.return !== null">{{ v.dollarValue > 10000 ? (v.dollarValue | currency:'USD':'symbol':'1.0-0') : (v.dollarValue | currency) }}</ng-container>
                              <ng-container *ngIf="v.return === null"><span class="text-muted">N/A</span></ng-container>
                            </td>
                            <td *ngIf="isColActive(activePeriodTab, 'return')" class="text-center"
                              [title]="v.return !== null ? (v.return | number:'1.2-5') + '%' : 'N/A'">
                              {{ v.return !== null ? (v.return | number:'1.2-2') + '%' : 'N/A' }}
                            </td>
                            <td *ngIf="isColActive(activePeriodTab, 'equity')" class="text-center">
                              <ng-container *ngIf="v.equity !== null">{{ v.equity }}%</ng-container>
                              <ng-container *ngIf="v.equity === null"><span class="text-muted">—</span></ng-container>
                            </td>
                            <td *ngIf="showFeeCol" class="text-center"
                              [title]="(isAnnualDetail(activePeriodTab) ? v.feeRateAnnual : v.feeRateMonthly) | number:'1.2-5'">
                              {{ (isAnnualDetail(activePeriodTab) ? v.feeRateAnnual : v.feeRateMonthly) > 0 ? ((isAnnualDetail(activePeriodTab) ? v.feeRateAnnual : v.feeRateMonthly) | number:'1.2-3') + '%' : '—' }}
                            </td>
                          </ng-container>
                        </ng-container>
                      </tr>
                    </ng-container>
                  </tbody>
                </table>
              </div>
              <div
                *ngIf="(isAnnualDetail(activePeriodTab) ? periodAnnualRows(activePeriodTab) : periodMonthlyRows(activePeriodTab)).length > 24 && !showAllMonthlyRows[activePeriodTab]"
                class="text-center mb-3">
                <button type="button" class="btn btn-outline-secondary btn-sm"
                  (click)="onShowAllMonthlyRowsChange(activePeriodTab)">
                  Show All
                  {{ (isAnnualDetail(activePeriodTab) ? periodAnnualRows(activePeriodTab) : periodMonthlyRows(activePeriodTab)).length }}
                  Rows
                </button>
              </div>

              <!-- Fund Descriptions -->
              <div *ngIf="fundDescriptions[activePeriodTab]?.length" class="mt-4">
                <h5 class="fw-semibold mb-3">Fund Descriptions</h5>
                <div class="table-responsive mb-4">
                  <table class="table table-sm table-bordered mb-0">
                    <thead class="table-light">
                      <tr>
                        <th>Fund</th>
                        <th>Description</th>
                        <th class="text-center" style="">Equity Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let f of fundDescriptions[activePeriodTab]">
                        <td class="" style="min-width:200px;">
                          <app-svg-sprite-v2 [title]="f.name" [width]="20" [height]="20"
                            [hash]="'#icon-' + svgSymbol(f.symbol)" style="flex-shrink:0"></app-svg-sprite-v2>
                          {{ f.name }}
                        </td>
                        <td [innerHTML]="f.description | safe:'html'" style="font-size:.85em"></td>
                        <td class="text-center" style="white-space:nowrap">
                          <ng-container *ngIf="f.equityMin !== null && f.equityMax !== null">
                            <ng-container *ngIf="f.equityMin === f.equityMax">{{ f.equityMin }}%</ng-container>
                            <ng-container *ngIf="f.equityMin !== f.equityMax">{{ f.equityMax }}% – {{ f.equityMin }}%
                            </ng-container>
                          </ng-container>
                          <ng-container *ngIf="f.equityMin === null"><span class="text-muted">—</span></ng-container>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </ng-container>
          </div>
        </div>

      </form>


    </div>
  </div>

  <hr>
  <div class="row mt-5">
    <app-important-disclosures></app-important-disclosures>
    <hr>


  </div>

</div>